import RelativeTime from "components/ui/RelativeTime";
import { LinkButton } from "lib/miolo";
import Link from "next/link";
import { useState } from "react";
import type { IAssetDiscussionCommentFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import type { IAssetDiscussionCommentReplyFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentReplyFields.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import translateTypenameIntoAssetType from "../../lib/utils/translateTypenameIntoAssetType";
import { IReactionSubject } from "../../types/graphqlTypes";
import Avatar from "../core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider } from "../core/menu";
import AssetDiscussionCommentReplyContour from "./AssetDiscussionCommentReplyContour";
import CommentActionsDropdown from "./CommentActionsDropdown";
import CommentContent from "./CommentContent";
import IndividualCommentInputForm from "./form/IndividualCommentInputForm";
import ReplyCommentInputForm from "./form/ReplyCommentInputForm";
import translateServerErrorIntoFriendlyMessage from "./form/translateServerErrorIntoFriendlyMessage";
import useCommentReplyActions from "./hooks/useCommentReplyActions";
import useReplyContour from "./hooks/useReplyContour";
import HorizontalLineDivider from "./HorizontalLineDivider";
import InlineAssetDiscussionCommentError from "./InlineAssetDiscussionCommentError";
import ReplyToAssetDiscussionCommentMutation from "./mutations/ReplyToAssetDiscussionCommentMutation";
import ThumbsUpReaction from "./reactions/ThumbsUpReaction";

interface IAssetDiscussionCommentReplyProps {
  reply: IAssetDiscussionCommentReplyFieldsFragment;
  rootComment: IAssetDiscussionCommentFieldsFragment;
  allowToReply?: boolean;
}

export default function AssetDiscussionCommentReply({
  reply,
  rootComment,
  allowToReply,
}: IAssetDiscussionCommentReplyProps) {
  const selfie = useSelfie();
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isWritingReply, setIsWritingReply] = useState<boolean>(false);

  const { destroyComment, editComment, isDestroyingComment, destroyCommentError, editCommentError } =
    useCommentReplyActions(reply, rootComment);

  const viewerCanTakeActions = reply.viewerCanEdit || reply.viewerCanDestroy;
  const hasContour = useReplyContour(reply.shortId);

  return (
    <div className="relative box-border">
      {hasContour && <AssetDiscussionCommentReplyContour />}

      <div className="flex items-start space-x-2">
        <Avatar
          src={reply.author.pictureUrl}
          hashValue={reply.author._id}
          alt={reply.author.fullName}
          size={40}
          squircle={true}
          variant="rounded"
        />

        <div className="flex flex-col">
          <strong className="text-sm dark:text-slate-400">{reply.author.fullName}</strong>

          <div>
            <Link
              href="/comments/[shortId]"
              as={`/comments/${rootComment.shortId}#${reply.shortId}`}
              passHref={true}
              className="hover:underline"
            >
              <RelativeTime className="text-xs font-medium text-slate-500 md:text-sm" timeStamp={reply.postedAt} />
            </Link>

            {reply.lastEditedAt && (
              <span className="text-xs font-medium text-slate-500 md:text-sm">{" • editado"}</span>
            )}
          </div>
        </div>
      </div>

      <div className="relative mt-4 space-y-2">
        {isEditingContent ? (
          <>
            <IndividualCommentInputForm
              submitButtonText="Editar"
              onSubmit={async (bodyAsPlainText) => {
                await editComment(bodyAsPlainText);
                setIsEditingContent(false);
              }}
              onCancel={() => setIsEditingContent(false)}
              bodyAsPlainText={reply.bodyAsPlainText}
            />

            <HorizontalLineDivider />
          </>
        ) : (
          <div className="pr-6">
            <CommentContent bodyAsPlainText={reply.bodyAsPlainText} />
          </div>
        )}

        <div className="pt-4">
          <ThumbsUpReaction
            subjectId={reply._id}
            subjectType={IReactionSubject.AssetDiscussionCommentReply}
            reactable={reply}
          />
        </div>

        <div
          className="pt-2"
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          <div
            css={{
              display: "grid",
              gridAutoColumns: "max-content",
              gridAutoFlow: "column",
              gridColumnGap: 12,
            }}
          >
            {rootComment.viewerCanReply && allowToReply && (
              <LinkButton variant="primary" onClick={() => setIsWritingReply(!isWritingReply)}>
                Responder
              </LinkButton>
            )}

            {viewerCanTakeActions && (
              <CommentActionsDropdown>
                {({ closeOnClick }) => (
                  <Menu>
                    {selfie && selfie.isSiteAdmin && (
                      <MenuActionButtonItem
                        intent="primary"
                        onClick={closeOnClick(() => {
                          navigator.clipboard.writeText(reply.author.email);
                        })}
                      >
                        Copiar email
                      </MenuActionButtonItem>
                    )}

                    {reply.viewerCanEdit && (
                      <MenuActionButtonItem
                        intent="primary"
                        onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                      >
                        Editar
                      </MenuActionButtonItem>
                    )}

                    <MenuDivider />

                    {reply.viewerCanDestroy && (
                      <MenuActionButtonItem
                        intent="danger"
                        disabled={isDestroyingComment}
                        onClick={() => destroyComment()}
                      >
                        {isDestroyingComment ? "Apagando..." : "Apagar"}
                      </MenuActionButtonItem>
                    )}
                  </Menu>
                )}
              </CommentActionsDropdown>
            )}
          </div>
        </div>

        {selfie && isWritingReply && (
          <div className="pt-2">
            <HorizontalLineDivider />

            <ReplyToAssetDiscussionCommentMutation>
              {(replyToAssetDiscussionComment) => (
                <div className="py-4">
                  <ReplyCommentInputForm
                    viewer={selfie}
                    autoFocus={true}
                    onCancel={() => setIsWritingReply(false)}
                    hintAboutSupportMessagesBeingDeleted={false}
                    onSubmit={async (bodyAsPlainText) => {
                      await replyToAssetDiscussionComment({
                        assetId: rootComment.asset._id,
                        assetType: translateTypenameIntoAssetType(rootComment.asset.__typename),
                        replyToCommentId: rootComment._id,
                        replyToCommentShortId: rootComment.shortId,
                        bodyAsPlainText,
                      });

                      setIsWritingReply(false);
                    }}
                  />
                </div>
              )}
            </ReplyToAssetDiscussionCommentMutation>
          </div>
        )}

        {firstError([destroyCommentError, editCommentError]) && (
          <div>
            <InlineAssetDiscussionCommentError>
              {translateServerErrorIntoFriendlyMessage(
                firstError([destroyCommentError, editCommentError]) ||
                  new Error("Whoopz...deu algo errado aqui. Tente novamente mais tarde."),
              )}
            </InlineAssetDiscussionCommentError>
          </div>
        )}
      </div>
    </div>
  );
}

function firstError(arrayOfErrors: (Error | null)[]): Error | null {
  return arrayOfErrors.find((error) => error !== null) || null;
}
